import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Grid, Paper, Typography, Box } from "@material-ui/core";

import UploadZone from "./UploadZone";
import ResultPanel from "./ResultPanel";
import {
  uploadStaffRoster,
  uploadStudentRoster,
} from "../../crud/api/onboarding.crud";

// TZ-789 admin: replaces the seven-step onboarding (create school →
// create each team → upload staff → assign each staff to a team →
// flag org admins → flag TeleHelp staff → upload students team by
// team) with two file uploads. Each file is uploaded independently
// and the backend processes row-by-row, returning a per-row result
// the admin can fix in-place and re-upload.

const Onboarding = ({ school }) => {
  const [staffResult, setStaffResult] = useState(null);
  const [studentResult, setStudentResult] = useState(null);

  const schoolId = school && school.id;
  const schoolMissing = !schoolId;

  // Reset both result panels when the admin switches school via the
  // selector. Otherwise the previously-uploaded school's per-row
  // result stays visible and looks like it applies to the new school.
  useEffect(() => {
    setStaffResult(null);
    setStudentResult(null);
  }, [schoolId]);

  const handleStaffUpload = (file) => {
    if (schoolMissing) return Promise.resolve();
    return uploadStaffRoster(setStaffResult, file, schoolId);
  };

  const handleStudentUpload = (file) => {
    if (schoolMissing) return Promise.resolve();
    return uploadStudentRoster(setStudentResult, file, schoolId);
  };

  return (
    <div>
      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          Roster Onboarding
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Two files, one school. Upload the staff roster and the student
          roster independently — either first, in any order, on different
          days. Each row is validated and either created, skipped (because
          someone with that email or phone is already on the roster), or
          returned as an error you can fix in the source file and re-upload.
        </Typography>
      </Box>

      {schoolMissing && (
        <Paper style={{ padding: 16, marginBottom: 16 }}>
          <Typography color="secondary">
            Select a school first to enable the upload zones.
          </Typography>
        </Paper>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <UploadZone
            title="Staff Roster"
            description="xlsx with columns: Name, Email, Phone, Role, Teams, Org Admin, TeleHelp visible, TeleHelp category."
            onUpload={handleStaffUpload}
            disabled={schoolMissing}
          />
          <ResultPanel result={staffResult} kind="staff" />
        </Grid>

        <Grid item xs={12} md={6}>
          <UploadZone
            title="Student Roster"
            description="xlsx with columns: Name, Email, Phone, Team, Date of Birth, Classification, Gender."
            onUpload={handleStudentUpload}
            disabled={schoolMissing}
          />
          <ResultPanel result={studentResult} kind="student" />
        </Grid>
      </Grid>
    </div>
  );
};

Onboarding.propTypes = {
  school: PropTypes.shape({
    id:   PropTypes.number,
    name: PropTypes.string,
  }),
};

Onboarding.defaultProps = {
  school: null,
};

const mapStateToProps = (store) => ({
  school: store.school.selected_school,
});

export default connect(mapStateToProps)(Onboarding);
